/**
 * @ Author: Housefolios
 * @ Create Time: 2021-09-16 13:21:50
 * @ Modified by: David Helmick
 * @ Modified time: 2024-07-31 17:07:44
 * @ Description: Using link in forgot password email users can change their password here
 */

import React, { useEffect, useRef, useState } from 'react'

import {
  Grid,
  Avatar,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Typography,
  Box,
} from '@mui/material'
import { useMutation } from '@apollo/client/react'
import { UPDATE_USER } from '@/housefolios-components/PersonalSettings/mutations'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { useParams } from 'react-router'
import {
  AuthCardHeader,
  AuthCardTitle,
  AuthNavLink,
  AuthPageShell,
  AuthPrimaryButton,
  authCardSx,
  authCardWrapSx,
  authPasswordToggleAvatarSx,
} from '../authChrome'

function ResetPasswordComponent(props) {
  const { token = '' } = useParams()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [newPasswordMask, setNewPasswordMask] = useState(true)
  const [confirmPasswordMask, setConfirmPasswordMask] = useState(true)
  const [tokenExpired, setTokenExpired] = useState(false)
  // Derived, not state: /ResetPassword with no token must never flash the form.
  const noToken = !token || token === 'undefined'

  useEffect(() => {
    ValidatorForm.addValidationRule('isPasswordMatch', (value) => {
      if (value !== newPassword) {
        return false
      }
      return true
    })
    return () => {
      ValidatorForm.removeValidationRule('isPasswordMatch')
    }
  })

  const ref = useRef(null)

  const checkPassword = async () => {
    if (ref.current && newPassword && confirmPassword)
      ref.current.isFormValid(false)
  }

  useEffect(() => {
    checkPassword()
  }, [newPassword])

  const [updateUser] = useMutation(UPDATE_USER)

  const onSubmit = (event) => {
    localStorage.setItem('token', token)
    updateUser({
      variables: {
        userInput: {
          password: newPassword,
          needPasswordReset: false,
        },
      },
    })
      .then(async ({ data  }) => {
        localStorage.setItem('token', data.updateUser.token)
        window.location.reload()
      })
      .catch((error) => {
        localStorage.removeItem('token')
        setTokenExpired(true)
        return error
      })
    event.preventDefault()
  }

  const passwordToggle = (masked, setMasked) => ({
    input: {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            onClick={() => setMasked(!masked)}
            size="small"
          >
            <Avatar sx={authPasswordToggleAvatarSx}>
              {masked ? <RemoveRedEyeIcon /> : <VisibilityOffIcon />}
            </Avatar>
          </IconButton>
        </InputAdornment>
      ),
    },
  })

  return (
    <AuthPageShell>
      <Grid sx={authCardWrapSx}>
        <Card sx={authCardSx}>
          <CardContent>
            {noToken ? (
              <Grid container direction="column" spacing={4}>
                <Grid>
                  <AuthCardHeader
                    title="Reset link invalid"
                    subtitle="This password reset link is missing or invalid."
                  />
                </Grid>
                <Grid>
                  <AuthPrimaryButton href="/ForgotPassword">
                    Request a new link
                  </AuthPrimaryButton>
                </Grid>
              </Grid>
            ) : tokenExpired ? (
              <Grid container direction="column" spacing={2}>
                <Grid>
                  <AuthCardTitle>Link expired</AuthCardTitle>
                </Grid>
                <Grid>
                  <Typography>
                    Please go to the{' '}
                    <AuthNavLink href="/ForgotPassword">Forgot Password</AuthNavLink>{' '}
                    page and enter the email address associated with your
                    account. If that doesn&apos;t work please contact the team
                    member that added you.
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Grid container direction="column" spacing={4}>
                <Grid>
                  <Grid container direction="column" spacing={2}>
                    <Grid>
                      <AuthCardTitle>Reset Password</AuthCardTitle>
                    </Grid>
                    <Grid>
                      <Typography>Create your new password!</Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid>
                  <ValidatorForm
                    ref={ref}
                    onSubmit={(event) => onSubmit(event)}
                  >
                    <Grid container direction="row" spacing={2}>
                      <Grid size={12}>
                        <TextValidator
                          fullWidth
                          variant="outlined"
                          label="New Password"
                          value={newPassword}
                          onChange={(event) =>
                            setNewPassword(event.target.value)
                          }
                          type={newPasswordMask ? 'password' : 'text'}
                          validators={[
                            'required',
                            'maxStringLength: 50',
                            'minStringLength: 7',
                          ]}
                          errorMessages={[
                            'This field is required',
                            'You cannot exceed 50 characters',
                            'Password must be at least 7 characters long',
                          ]}
                          slotProps={passwordToggle(
                            newPasswordMask,
                            setNewPasswordMask,
                          )}
                        />
                      </Grid>
                      <Grid size={12}>
                        <TextValidator
                          fullWidth
                          variant="outlined"
                          label="Confirm Password"
                          value={confirmPassword}
                          onChange={(event) =>
                            setConfirmPassword(event.target.value)
                          }
                          validators={[
                            'required',
                            'maxStringLength: 50',
                            'minStringLength: 7',
                            'isPasswordMatch',
                          ]}
                          type={
                            confirmPasswordMask ? 'password' : 'text'
                          }
                          errorMessages={[
                            'This field is required',
                            'You cannot exceed 50 characters',
                            'Password must be at least 7 characters long',
                            'Does not match password',
                          ]}
                          slotProps={passwordToggle(
                            confirmPasswordMask,
                            setConfirmPasswordMask,
                          )}
                        />
                      </Grid>
                      <Grid size={12}>
                        <AuthPrimaryButton type="submit">
                          Create Password
                        </AuthPrimaryButton>
                      </Grid>
                    </Grid>
                  </ValidatorForm>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
    </AuthPageShell>
  )
}

export default ResetPasswordComponent
